import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Loader2, Save, Wallet, TrendingUp, RotateCcw, Receipt } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_LABELS = {
  buyer_absorbs: 'Buyer absorbs (added to checkout)',
  organizer_absorbs: 'Organizer absorbs (eat the fee)',
  off: 'Off — no fees',
};

const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function TicketFeesPanel({ eventId, token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [previewQty, setPreviewQty] = useState(2);
  const [previewSubtotal, setPreviewSubtotal] = useState(50); // dollars
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/admin/ticket-fees/defaults`, auth),
        axios.get(`${API}/events/${eventId}/ticket-fees`, auth),
      ]);
      setDefaults(d.data.defaults);
      setCfg(c.data.config);
      setIsCustom(!!c.data.is_custom);
    } catch (e) {
      toast.error('Failed to load ticket fee config');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => { load(); }, [load]);

  // Live preview — recompute on cfg change
  useEffect(() => {
    if (!cfg) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API}/events/${eventId}/ticket-fees/preview`,
          { subtotal_cents: Math.round((parseFloat(previewSubtotal) || 0) * 100), ticket_qty: previewQty },
          auth
        );
        if (!cancelled) setPreview(res.data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, previewQty, previewSubtotal, eventId]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/events/${eventId}/ticket-fees`, cfg, auth);
      setCfg(res.data.config);
      setIsCustom(true);
      toast.success('Ticket fees saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const resetToDefaults = () => {
    if (!defaults) return;
    setCfg({ ...defaults });
  };

  if (loading || !cfg) {
    return <div className="py-10 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>;
  }

  const set = (patch) => setCfg(c => ({ ...c, ...patch }));

  return (
    <div className="space-y-4" data-testid="ticket-fees-panel">
      {/* Industry-standard explainer */}
      <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3 text-[11px] text-indigo-300 leading-relaxed">
        <strong>Why this matters:</strong> Stripe charges <code>2.9% + $0.30</code> per transaction. Industry default is to pass that to the buyer (Eventbrite charges 3.7% + $1.79). Our default of <code>3% + $1.50/ticket</code> covers Stripe and leaves a small platform margin. Adjust per-event below.
      </div>

      {/* Status pill */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={isCustom ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' : 'bg-slate-500/15 text-slate-400 border border-slate-500/30'} data-testid="fees-status-badge">
          {isCustom ? 'Using event override' : 'Using platform defaults'}
        </Badge>
        {isCustom && (
          <Button variant="outline" size="sm" onClick={resetToDefaults} className="h-7 text-xs" data-testid="fees-reset-btn">
            <RotateCcw className="h-3 w-3 mr-1" /> Reset to defaults
          </Button>
        )}
      </div>

      <Card className="border-border">
        <CardContent className="p-4 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Percent of subtotal</Label>
              <div className="relative">
                <Input
                  type="number" step="0.1" min="0" max="50"
                  value={cfg.percent}
                  onChange={e => set({ percent: parseFloat(e.target.value || 0) })}
                  data-testid="fees-percent-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Flat per ticket</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                <Input
                  type="number" step="0.01" min="0"
                  value={(cfg.flat_cents || 0) / 100}
                  onChange={e => set({ flat_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                  className="pl-6"
                  data-testid="fees-flat-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mode</Label>
              <select
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                value={cfg.mode}
                onChange={e => set({ mode: e.target.value })}
                data-testid="fees-mode-select"
              >
                {Object.entries(MODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border">
            <div>
              <Label className="text-xs cursor-pointer">Fees enabled</Label>
              <p className="text-[10px] text-muted-foreground">Turn off entirely (e.g. RSVP-only events).</p>
            </div>
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="fees-enabled-switch" />
          </div>

          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-fees-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save ticket fees
          </Button>
        </CardContent>
      </Card>

      {/* Live preview */}
      <Card className="border-border" data-testid="fees-preview-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-amber-400" />
            <h4 className="text-sm font-semibold">Live preview</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Sample ticket subtotal</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                <Input
                  type="number" min="0" step="1"
                  value={previewSubtotal}
                  onChange={e => setPreviewSubtotal(e.target.value)}
                  className="pl-6"
                  data-testid="fees-preview-subtotal"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tickets</Label>
              <Input
                type="number" min="1" step="1"
                value={previewQty}
                onChange={e => setPreviewQty(parseInt(e.target.value || 1, 10))}
                data-testid="fees-preview-qty"
              />
            </div>
          </div>

          {preview && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-border">
              <Stat icon={Wallet} label="Subtotal" value={fmt(preview.subtotal_cents)} testid="fp-subtotal" />
              <Stat icon={Receipt} label="Service fee" value={fmt(preview.fee_cents)} accent="text-amber-400" testid="fp-fee" />
              <Stat icon={TrendingUp} label="Buyer pays" value={fmt(preview.buyer_total_cents)} accent="text-indigo-400" testid="fp-buyer" />
              <Stat icon={Wallet} label="You receive" value={fmt(preview.organizer_net_cents)} accent="text-emerald-400" testid="fp-organizer" />
            </div>
          )}

          {preview && (
            <p className="text-[10px] text-muted-foreground">
              Fee paid by: <strong className="text-foreground capitalize">{preview.fee_paid_by}</strong>.
              {preview.fee_paid_by === 'buyer' && ' Buyer sees a transparent "Service fee" line at Stripe checkout.'}
              {preview.fee_paid_by === 'organizer' && ' Buyer sees clean pricing; the fee comes out of your net at payout.'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wide">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-lg font-bold tabular-nums ${accent || ''}`}>{value}</div>
    </div>
  );
}
